import { extMimeMap, getAssetName } from '@blocksuite/store';
import * as fflate from 'fflate';
import { FAILSAFE_SCHEMA, load as loadYaml } from 'js-yaml';

export class Zip {
  private compressed = new Uint8Array();

  private finalize?: () => void;

  private finalized = false;

  private readonly zip = new fflate.Zip((err, chunk, final) => {
    if (!err) {
      const temp = new Uint8Array(this.compressed.length + chunk.length);
      temp.set(this.compressed);
      temp.set(chunk, this.compressed.length);
      this.compressed = temp;
    }
    if (final) {
      this.finalized = true;
      this.finalize?.();
    }
  });

  async file(path: string, content: Blob | File | string) {
    const deflate = new fflate.ZipDeflate(path);
    this.zip.add(deflate);
    if (typeof content === 'string') {
      deflate.push(fflate.strToU8(content), true);
    } else {
      deflate.push(new Uint8Array(await content.arrayBuffer()), true);
    }
  }

  folder(folderPath: string) {
    return {
      folder: (folderPath2: string) => {
        return this.folder(`${folderPath}/${folderPath2}`);
      },
      file: async (name: string, blob: Blob) => {
        await this.file(`${folderPath}/${name}`, blob);
      },
      generate: async () => {
        return this.generate();
      },
    };
  }

  async generate() {
    this.zip.end();
    return new Promise<Blob>(resolve => {
      if (this.finalized) {
        resolve(new Blob([this.compressed], { type: 'application/zip' }));
      } else {
        this.finalize = () =>
          resolve(new Blob([this.compressed], { type: 'application/zip' }));
      }
    });
  }
}

const CENTRAL_DIRECTORY_FILE_HEADER_SIGNATURE = 0x02014b50;
const END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50;
const LOCAL_FILE_HEADER_SIGNATURE = 0x04034b50;
const ZIP64_END_OF_CENTRAL_DIRECTORY_LOCATOR_SIGNATURE = 0x07064b50;
const ZIP64_END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06064b50;
const ZIP64_EXTRA_FIELD_ID = 0x0001;
const UNICODE_PATH_EXTRA_FIELD_ID = 0x7075;
const UTF8_FILENAME_FLAG = 1 << 11;
const ENCRYPTED_ENTRY_FLAG = 1;
const DOS_HOST_OS = 0;
const WINDOWS_NTFS_HOST_OS = 10;
const VFAT_HOST_OS = 14;
const MAX_UINT16 = 0xffff;
const MAX_UINT32 = 0xffffffff;

type ParsedZipEntry = {
  path: string;
  flags: number;
  compression: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
};

type CentralDirectoryInfo = {
  entryCount: number;
  offset: number;
  size: number;
};

export class Unzip {
  private entries?: Array<{ path: string; bytes: Uint8Array }>;

  async load(blob: Blob) {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    this.entries = readZipEntries(bytes).map(entry => ({
      path: entry.path,
      bytes: extractZipEntry(bytes, entry),
    }));
  }

  *[Symbol.iterator]() {
    const entries = this.entries ?? [];
    let index = 0;
    for (const { path, bytes } of entries) {
      if (path.includes('__MACOSX') || path.includes('DS_Store')) {
        continue;
      }
      const lastSplitIndex = path.lastIndexOf('/');
      const fileName = path.substring(lastSplitIndex + 1);
      const fileExt =
        fileName.lastIndexOf('.') === -1 ? '' : fileName.split('.').at(-1);
      const mime = extMimeMap.get(fileExt ?? '');
      const content = new File(
        [bytes],
        fileName,
        mime ? { type: mime } : undefined
      ) as Blob;

      yield { path, content, index };
      index++;
    }
  }
}

function readZipEntries(bytes: Uint8Array): ParsedZipEntry[] {
  const centralDirectory = readCentralDirectoryInfo(bytes);
  const directoryEnd = centralDirectory.offset + centralDirectory.size;
  if (
    !Number.isSafeInteger(directoryEnd) ||
    directoryEnd > bytes.length ||
    centralDirectory.entryCount > Math.floor(centralDirectory.size / 46)
  ) {
    throw new Error('ZIP central directory is out of bounds');
  }

  const entries: ParsedZipEntry[] = [];
  const normalizedPaths = new Set<string>();
  let offset = centralDirectory.offset;

  for (let index = 0; index < centralDirectory.entryCount; index++) {
    assertRange(bytes, offset, 46, 'ZIP central directory entry');
    if (readU32(bytes, offset) !== CENTRAL_DIRECTORY_FILE_HEADER_SIGNATURE) {
      throw new Error('Invalid ZIP central directory entry');
    }

    const madeBy = readU16(bytes, offset + 4);
    const flags = readU16(bytes, offset + 8);
    const compression = readU16(bytes, offset + 10);
    const compressedSize32 = readU32(bytes, offset + 20);
    const uncompressedSize32 = readU32(bytes, offset + 24);
    const fileNameLength = readU16(bytes, offset + 28);
    const extraLength = readU16(bytes, offset + 30);
    const commentLength = readU16(bytes, offset + 32);
    const localHeaderOffset32 = readU32(bytes, offset + 42);
    const fileNameStart = offset + 46;
    const fileNameEnd = fileNameStart + fileNameLength;
    const extraEnd = fileNameEnd + extraLength;
    const nextOffset = extraEnd + commentLength;

    if (nextOffset > directoryEnd || nextOffset > bytes.length) {
      throw new Error('ZIP central directory entry is out of bounds');
    }

    const rawName = bytes.slice(fileNameStart, fileNameEnd);
    const extra = bytes.slice(fileNameEnd, extraEnd);
    const { compressedSize, uncompressedSize, localHeaderOffset } =
      readZip64EntryValues(extra, {
        compressedSize: compressedSize32,
        uncompressedSize: uncompressedSize32,
        localHeaderOffset: localHeaderOffset32,
      });
    const decodedName = decodeZipEntryName({
      rawName,
      extra,
      flags,
      hostOs: madeBy >>> 8,
    });
    const path = normalizeZipEntryPath(decodedName);
    const duplicateKey = path.endsWith('/') ? path.slice(0, -1) : path;

    if (normalizedPaths.has(duplicateKey)) {
      throw new Error(`ZIP contains duplicate path: ${path}`);
    }
    normalizedPaths.add(duplicateKey);

    entries.push({
      path,
      flags,
      compression,
      compressedSize,
      uncompressedSize,
      localHeaderOffset,
    });
    offset = nextOffset;
  }

  return entries;
}

function readCentralDirectoryInfo(bytes: Uint8Array): CentralDirectoryInfo {
  const endOfDirectoryOffset = findEndOfCentralDirectory(bytes);
  const entryCount = readU16(bytes, endOfDirectoryOffset + 10);
  const size = readU32(bytes, endOfDirectoryOffset + 12);
  const offset = readU32(bytes, endOfDirectoryOffset + 16);

  if (
    entryCount !== MAX_UINT16 &&
    size !== MAX_UINT32 &&
    offset !== MAX_UINT32
  ) {
    return { entryCount, size, offset };
  }

  const locatorOffset = endOfDirectoryOffset - 20;
  assertRange(
    bytes,
    locatorOffset,
    20,
    'ZIP64 end of central directory locator'
  );
  if (
    readU32(bytes, locatorOffset) !==
    ZIP64_END_OF_CENTRAL_DIRECTORY_LOCATOR_SIGNATURE
  ) {
    throw new Error('ZIP64 end of central directory locator is missing');
  }

  const zip64DirectoryOffset = readU64(bytes, locatorOffset + 8);
  assertRange(
    bytes,
    zip64DirectoryOffset,
    56,
    'ZIP64 end of central directory'
  );
  if (
    readU32(bytes, zip64DirectoryOffset) !==
    ZIP64_END_OF_CENTRAL_DIRECTORY_SIGNATURE
  ) {
    throw new Error('Invalid ZIP64 end of central directory');
  }

  return {
    entryCount: readU64(bytes, zip64DirectoryOffset + 32),
    size: readU64(bytes, zip64DirectoryOffset + 40),
    offset: readU64(bytes, zip64DirectoryOffset + 48),
  };
}

function findEndOfCentralDirectory(bytes: Uint8Array) {
  const firstPossibleOffset = Math.max(0, bytes.length - 0xffff - 22);
  for (
    let offset = bytes.length - 22;
    offset >= firstPossibleOffset;
    offset--
  ) {
    if (readU32(bytes, offset) !== END_OF_CENTRAL_DIRECTORY_SIGNATURE) {
      continue;
    }
    const commentLength = readU16(bytes, offset + 20);
    if (offset + 22 + commentLength === bytes.length) {
      return offset;
    }
  }
  throw new Error('ZIP end of central directory is missing');
}

function readZip64EntryValues(
  extra: Uint8Array,
  values: {
    compressedSize: number;
    uncompressedSize: number;
    localHeaderOffset: number;
  }
) {
  const needsZip64 =
    values.compressedSize === MAX_UINT32 ||
    values.uncompressedSize === MAX_UINT32 ||
    values.localHeaderOffset === MAX_UINT32;
  if (!needsZip64) {
    return values;
  }

  const zip64 = getExtraField(extra, ZIP64_EXTRA_FIELD_ID);
  if (!zip64) {
    throw new Error('ZIP64 entry is missing its extended information field');
  }

  let offset = 0;
  const readNext = () => {
    if (offset + 8 > zip64.length) {
      throw new Error('ZIP64 extended information field is truncated');
    }
    const value = readU64(zip64, offset);
    offset += 8;
    return value;
  };

  return {
    uncompressedSize:
      values.uncompressedSize === MAX_UINT32
        ? readNext()
        : values.uncompressedSize,
    compressedSize:
      values.compressedSize === MAX_UINT32 ? readNext() : values.compressedSize,
    localHeaderOffset:
      values.localHeaderOffset === MAX_UINT32
        ? readNext()
        : values.localHeaderOffset,
  };
}

function decodeZipEntryName({
  rawName,
  extra,
  flags,
  hostOs,
}: {
  rawName: Uint8Array;
  extra: Uint8Array;
  flags: number;
  hostOs: number;
}) {
  if (flags & UTF8_FILENAME_FLAG) {
    return decodeUtf8(rawName, 'UTF-8 ZIP filename');
  }

  const unicodePath = decodeUnicodePathExtraField(extra, rawName);
  if (unicodePath !== undefined) {
    return unicodePath;
  }

  const hasExtendedBytes = rawName.some(byte => byte > 0x7f);
  const windowsLegacyEncoding =
    hostOs === DOS_HOST_OS ||
    hostOs === WINDOWS_NTFS_HOST_OS ||
    hostOs === VFAT_HOST_OS;

  // Windows ZIP writers can omit bit 11 for UTF-8 names. Keep strict UTF-8
  // when it is valid, then fall back to the Windows legacy code page.
  if (hasExtendedBytes) {
    const utf8Name = tryDecodeUtf8(rawName);
    if (utf8Name !== undefined) {
      return utf8Name;
    }
  }

  if (hasExtendedBytes && windowsLegacyEncoding) {
    return decodeWindowsLegacyFilename(rawName);
  }

  return decodeRawBytes(rawName);
}

function decodeUnicodePathExtraField(extra: Uint8Array, rawName: Uint8Array) {
  const unicodePath = getExtraField(extra, UNICODE_PATH_EXTRA_FIELD_ID);
  if (!unicodePath || unicodePath.length < 5 || unicodePath[0] !== 1) {
    return undefined;
  }
  if (readU32(unicodePath, 1) !== crc32(rawName)) {
    return undefined;
  }
  return decodeUtf8(unicodePath.subarray(5), 'ZIP Unicode path extra field');
}

function decodeUtf8(bytes: Uint8Array, label: string) {
  const decoded = tryDecodeUtf8(bytes);
  if (decoded !== undefined) {
    return decoded;
  }
  throw new Error(`Invalid ${label}`);
}

function tryDecodeUtf8(bytes: Uint8Array) {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return undefined;
  }
}

function decodeWindowsLegacyFilename(bytes: Uint8Array) {
  try {
    return new TextDecoder('gb18030', { fatal: true }).decode(bytes);
  } catch {
    return decodeRawBytes(bytes);
  }
}

function decodeRawBytes(bytes: Uint8Array) {
  let decoded = '';
  for (const byte of bytes) {
    decoded += String.fromCharCode(byte);
  }
  return decoded;
}

function normalizeZipEntryPath(path: string) {
  const normalizedSeparators = path.replace(/\\/g, '/');
  if (
    normalizedSeparators.startsWith('/') ||
    /^[a-z]:/i.test(normalizedSeparators)
  ) {
    throw new Error(`ZIP contains an absolute path: ${path}`);
  }

  const isDirectory = normalizedSeparators.endsWith('/');
  const segments: string[] = [];
  for (const segment of normalizedSeparators.split('/')) {
    if (!segment || segment === '.') {
      continue;
    }
    if (segment === '..') {
      throw new Error(`ZIP contains a traversal path: ${path}`);
    }
    if (segment.includes('\0')) {
      throw new Error('ZIP filename contains a null byte');
    }
    segments.push(segment);
  }

  if (!segments.length) {
    throw new Error('ZIP filename is empty');
  }
  return `${segments.join('/')}${isDirectory ? '/' : ''}`;
}

function extractZipEntry(bytes: Uint8Array, entry: ParsedZipEntry) {
  if (entry.flags & ENCRYPTED_ENTRY_FLAG) {
    throw new Error(`Encrypted ZIP entries are not supported: ${entry.path}`);
  }

  const localHeaderOffset = entry.localHeaderOffset;
  assertRange(bytes, localHeaderOffset, 30, 'ZIP local file header');
  if (readU32(bytes, localHeaderOffset) !== LOCAL_FILE_HEADER_SIGNATURE) {
    throw new Error(`Invalid ZIP local file header: ${entry.path}`);
  }

  const fileNameLength = readU16(bytes, localHeaderOffset + 26);
  const extraLength = readU16(bytes, localHeaderOffset + 28);
  const dataOffset = localHeaderOffset + 30 + fileNameLength + extraLength;
  assertRange(bytes, dataOffset, entry.compressedSize, 'ZIP entry data');
  const compressed = bytes.subarray(
    dataOffset,
    dataOffset + entry.compressedSize
  );

  let content: Uint8Array;
  if (entry.compression === 0) {
    if (entry.compressedSize !== entry.uncompressedSize) {
      throw new Error(`Invalid stored ZIP entry size: ${entry.path}`);
    }
    content = compressed.slice();
  } else if (entry.compression === 8) {
    content = fflate.inflateSync(compressed, {
      out: new Uint8Array(entry.uncompressedSize),
    });
  } else {
    throw new Error(`Unknown ZIP compression type: ${entry.compression}`);
  }

  if (content.byteLength !== entry.uncompressedSize) {
    throw new Error(`Invalid ZIP entry size: ${entry.path}`);
  }
  return content;
}

function getExtraField(extra: Uint8Array, fieldId: number) {
  let offset = 0;
  while (offset < extra.length) {
    assertRange(extra, offset, 4, 'ZIP extra field header');
    const id = readU16(extra, offset);
    const size = readU16(extra, offset + 2);
    const dataOffset = offset + 4;
    assertRange(extra, dataOffset, size, 'ZIP extra field');
    if (id === fieldId) {
      return extra.subarray(dataOffset, dataOffset + size);
    }
    offset = dataOffset + size;
  }
  return undefined;
}

function crc32(bytes: Uint8Array) {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit++) {
      crc = crc & 1 ? (crc >>> 1) ^ 0xedb88320 : crc >>> 1;
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function readU16(bytes: Uint8Array, offset: number) {
  assertRange(bytes, offset, 2, 'ZIP integer');
  return bytes[offset]! | (bytes[offset + 1]! << 8);
}

function readU32(bytes: Uint8Array, offset: number) {
  assertRange(bytes, offset, 4, 'ZIP integer');
  return (
    (bytes[offset]! |
      (bytes[offset + 1]! << 8) |
      (bytes[offset + 2]! << 16) |
      (bytes[offset + 3]! << 24)) >>>
    0
  );
}

function readU64(bytes: Uint8Array, offset: number) {
  assertRange(bytes, offset, 8, 'ZIP integer');
  const value = readU32(bytes, offset) + readU32(bytes, offset + 4) * 2 ** 32;
  if (!Number.isSafeInteger(value)) {
    throw new Error('ZIP64 integer exceeds JavaScript safe range');
  }
  return value;
}

function assertRange(
  bytes: Uint8Array,
  offset: number,
  length: number,
  label: string
) {
  if (offset < 0 || length < 0 || offset + length > bytes.length) {
    throw new Error(`${label} is out of bounds`);
  }
}

export async function createAssetsArchive(
  assetsMap: Map<string, Blob>,
  assetsIds: string[]
) {
  const zip = new Zip();

  for (const [id, blob] of assetsMap) {
    if (!assetsIds.includes(id)) continue;
    const name = getAssetName(assetsMap, id);
    await zip.folder('assets').file(name, blob);
  }

  return zip;
}

export function download(blob: Blob, name: string) {
  const element = document.createElement('a');
  element.setAttribute('download', name);
  const fileURL = URL.createObjectURL(blob);
  element.setAttribute('href', fileURL);
  element.style.display = 'none';
  document.body.append(element);
  element.click();
  element.remove();
  URL.revokeObjectURL(fileURL);
}

const metaMatcher = /(?<=---)(.*?)(?=---)/ms;
const bodyMatcher = /---.*?---/s;
export const parseMatter = (contents: string) => {
  const matterMatch = contents.match(metaMatcher);
  if (!matterMatch || !matterMatch[0]) return null;
  const metadata = loadYaml(matterMatch[0], { schema: FAILSAFE_SCHEMA });
  if (!metadata || typeof metadata !== 'object') return null;
  const body = contents.replace(bodyMatcher, '');
  return { matter: matterMatch[0], body, metadata };
};
